class MonsgaProductDetails extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';
    this.tabs = [...this.querySelectorAll('[data-mpd-tab]')];
    this.panels = [...this.querySelectorAll('[data-mpd-panel]')];
    this.navigation = this.querySelector('.mpd__tabs');
    this.specToggles = [...this.querySelectorAll('[data-mpd-spec-toggle]')];
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', (event) => this.onTabClick(event, tab));
      tab.addEventListener('keydown', (event) => this.onNavigationKeydown(event, tab));
    });
    this.specToggles.forEach((toggle) => {
      toggle.addEventListener('click', () => this.toggleSpecGroup(toggle));
    });

    window.addEventListener('resize', this.onResize, { passive: true });
    this.bindScrollContainer();
    this.onResize();
    this.updateActivePanel();
  }

  disconnectedCallback() {
    this.scrollEventTarget?.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
  }

  onResize() {
    this.bindScrollContainer();
    this.measureLayout();
    this.onScroll();
  }

  onScroll() {
    if (this.scrollFrame) return;
    this.scrollFrame = window.requestAnimationFrame(() => {
      this.scrollFrame = null;
      this.measureLayout();
      this.updateActivePanel();
    });
  }

  getScrollContainer() {
    const pageWrapper = document.querySelector('.page-wrapper');

    if (pageWrapper) {
      const overflowY = getComputedStyle(pageWrapper).overflowY;
      const hasScrollableOverflow = /(auto|scroll|overlay)/.test(overflowY);

      if (hasScrollableOverflow && pageWrapper.scrollHeight > pageWrapper.clientHeight + 1) {
        return pageWrapper;
      }
    }

    return document.scrollingElement || document.documentElement;
  }

  isDocumentScroller(container = this.scrollContainer) {
    return !container ||
      container === document.scrollingElement ||
      container === document.documentElement ||
      container === document.body;
  }

  bindScrollContainer() {
    const nextContainer = this.getScrollContainer();
    const nextTarget = this.isDocumentScroller(nextContainer) ? window : nextContainer;

    if (nextTarget === this.scrollEventTarget) {
      this.scrollContainer = nextContainer;
      return;
    }

    this.scrollEventTarget?.removeEventListener('scroll', this.onScroll);
    this.scrollContainer = nextContainer;
    this.scrollEventTarget = nextTarget;
    this.scrollEventTarget.addEventListener('scroll', this.onScroll, { passive: true });
  }

  getContainerTop() {
    return this.isDocumentScroller() ? 0 : this.scrollContainer.getBoundingClientRect().top;
  }

  getScrollTop() {
    if (this.isDocumentScroller()) {
      return window.scrollY || this.scrollContainer?.scrollTop || 0;
    }

    return this.scrollContainer.scrollTop;
  }

  measureLayout() {
    const header = document.querySelector('#header-component');
    const headerRect = header?.getBoundingClientRect();
    const containerTop = this.getContainerTop();
    const headerIsHidden = header?.dataset.stickyState === 'idle';
    const headerHeight = headerRect && !headerIsHidden
      ? Math.max(0, Math.min(headerRect.height, headerRect.bottom - containerTop))
      : 0;

    this.style.setProperty('--mpd-header-offset', `${Math.round(headerHeight)}px`);
    this.scrollOffset = headerHeight + (this.navigation?.offsetHeight || 0) + 18;
    this.style.setProperty('--mpd-scroll-offset', `${Math.round(this.scrollOffset)}px`);
  }

  updateActivePanel() {
    if (!this.panels.length) return;
    const marker = this.getContainerTop() + (this.scrollOffset || 82);
    let activePanel = this.panels[0];

    this.panels.forEach((panel) => {
      if (panel.getBoundingClientRect().top <= marker + 24) activePanel = panel;
    });

    this.setActive(activePanel.dataset.mpdPanel);
  }

  setActive(name) {
    this.tabs.forEach((tab) => {
      tab.setAttribute('aria-current', String(tab.dataset.mpdTab === name));
    });
  }

  onTabClick(event, tab) {
    event.preventDefault();
    event.stopPropagation();
    this.scrollToPanel(tab.dataset.mpdTab);
  }

  scrollToPanel(name) {
    const panel = this.panels.find((item) => item.dataset.mpdPanel === name);
    if (!panel) return;

    this.bindScrollContainer();
    this.measureLayout();
    this.setActive(name);
    const top = this.getScrollTop() + panel.getBoundingClientRect().top - this.getContainerTop() - (this.scrollOffset || 82);
    const options = { top: Math.max(0, top), behavior: 'auto' };

    if (this.isDocumentScroller()) window.scrollTo(options);
    else this.scrollContainer.scrollTo(options);
  }

  onNavigationKeydown(event, tab) {
    const index = this.tabs.indexOf(tab);
    if (index < 0) return;

    let next = index;
    if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
    else if (event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = this.tabs.length - 1;
    else return;

    event.preventDefault();
    this.tabs[next].focus({ preventScroll: true });
  }

  toggleSpecGroup(toggle) {
    const group = toggle.dataset.specGroup;
    if (!group) return;

    const expanded = toggle.getAttribute('aria-expanded') !== 'false';
    toggle.setAttribute('aria-expanded', String(!expanded));
    this.querySelectorAll(`[data-mpd-spec-row][data-spec-group="${CSS.escape(group)}"]`).forEach((row) => {
      row.hidden = expanded;
    });
  }
}

if (!customElements.get('monsga-product-details-scroll-v3')) {
  customElements.define('monsga-product-details-scroll-v3', MonsgaProductDetails);
}

if (!customElements.get('monsga-product-details-scroll-v2')) {
  customElements.define('monsga-product-details-scroll-v2', class extends MonsgaProductDetails {});
}

if (!customElements.get('monsga-product-details-scroll')) {
  customElements.define('monsga-product-details-scroll', class extends MonsgaProductDetails {});
}

if (!customElements.get('monsga-product-details')) {
  customElements.define('monsga-product-details', class extends MonsgaProductDetails {});
}

class MonsgaAplusCarousel extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';
    this.track = this.querySelector('[data-aplus-track]');
    this.slides = [...this.querySelectorAll('[data-aplus-slide]')];
    this.previousButton = this.querySelector('[data-aplus-previous]');
    this.nextButton = this.querySelector('[data-aplus-next]');
    this.currentLabel = this.querySelector('[data-aplus-current]');
    this.moduleTabs = [...(this.closest('.mpd__aplus-module')?.querySelectorAll('[data-aplus-select]') || [])];
    this.current = Math.max(0, this.slides.findIndex((slide) => slide.getAttribute('aria-hidden') === 'false'));
    this.scrollFrame = null;

    this.previousButton?.addEventListener('click', () => this.select(this.current - 1));
    this.nextButton?.addEventListener('click', () => this.select(this.current + 1));
    this.moduleTabs.forEach((tab) => {
      tab.addEventListener('click', () => this.select(Number(tab.dataset.aplusSelect)));
      tab.addEventListener('keydown', (event) => this.onTabKeydown(event, tab));
    });
    this.addEventListener('keydown', (event) => this.onKeydown(event));
    this.track?.addEventListener('scroll', () => this.onScroll(), { passive: true });
    this.setAttribute('tabindex', '0');
    this.update(this.current);
  }

  select(index) {
    if (!this.slides.length) return;
    const next = (index + this.slides.length) % this.slides.length;
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    this.track?.scrollTo({ left: this.slides[next].offsetLeft, behavior });
    this.update(next);
  }

  update(index) {
    this.current = index;
    this.slides.forEach((slide, slideIndex) => {
      slide.setAttribute('aria-hidden', String(slideIndex !== this.current));
    });
    if (this.currentLabel) this.currentLabel.textContent = String(this.current + 1);
    this.moduleTabs.forEach((tab, tabIndex) => {
      tab.setAttribute('aria-selected', String(tabIndex === this.current));
    });
  }

  onScroll() {
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    this.scrollFrame = requestAnimationFrame(() => {
      const current = this.slides.reduce((closest, slide, index) => (
        Math.abs(slide.offsetLeft - this.track.scrollLeft) < Math.abs(this.slides[closest].offsetLeft - this.track.scrollLeft)
          ? index
          : closest
      ), 0);
      if (current !== this.current) this.update(current);
    });
  }

  onKeydown(event) {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.select(this.current - 1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.select(this.current + 1);
    }
  }

  onTabKeydown(event, tab) {
    const index = this.moduleTabs.indexOf(tab);
    if (index < 0) return;
    let next = index;
    if (event.key === 'ArrowRight') next = (index + 1) % this.moduleTabs.length;
    else if (event.key === 'ArrowLeft') next = (index - 1 + this.moduleTabs.length) % this.moduleTabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = this.moduleTabs.length - 1;
    else return;
    event.preventDefault();
    this.moduleTabs[next].focus({ preventScroll: true });
    this.select(next);
  }

}

if (!customElements.get('monsga-aplus-carousel')) {
  customElements.define('monsga-aplus-carousel', MonsgaAplusCarousel);
}
